import { useState, useEffect, useCallback } from 'react';
import { teamsListService } from '../services/teamsList.service';
import type { TeamListItem, TeamsListFilters, TeamsListKPI } from '../types/teamsList.types';
import { useAuth } from '@/core/auth/AuthContext';

export function useTeamsList(companyId: string) {
  const { user } = useAuth();
  const userId = user?.id || '';
  
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [kpis, setKpis] = useState<TeamsListKPI>({ total: 0, active: 0, paused: 0, disbanded: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<TeamsListFilters>({
    search: '',
    category: 'all',
    status: 'all',
    department: 'all'
  });
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });

  const fetchData = useCallback(async () => {
    if (!companyId || !userId) return;
    setIsLoading(true);
    try {
      const [teamsRes, kpiRes] = await Promise.all([
        teamsListService.getTeams(companyId, filters, userId),
        teamsListService.getKPIs(companyId, userId)
      ]);
      setTeams(teamsRes.data);
      setKpis(kpiRes);
      setPagination(prev => ({ ...prev, total: teamsRes.total }));
    } catch (error) {
      console.error('[useTeamsList] Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, filters, userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const deleteTeam = async (id: string) => {
    if (!userId) return;
    try {
      await teamsListService.deleteTeam(id, companyId, userId);
      await fetchData();
    } catch (error) {
      console.error('[useTeamsList] Error deleting team:', error);
      throw error;
    }
  };

  return {
    teams,
    kpis,
    isLoading,
    filters,
    setFilters,
    pagination,
    setPagination,
    deleteTeam,
    refresh: fetchData
  };
}
